import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { Observable } from "rxjs";
import { retry, switchMap } from "rxjs/operators";
import {
  AbstractCirculationsService,
  EntityId,
  ICirculationTemplate,
  ICirculationTemplateStep,
  IMetaData
} from "../interfaces";
import { LogClass } from "../logger";
import {
  selectMappedStepId,
  selectPrepareId,
  selectTemplateId
} from "../state";

@LogClass({ source: "TemplateFacade" })
@Injectable({
  providedIn: "root"
})
export class TemplateFacade {
  constructor(
    private circulationService: AbstractCirculationsService,
    private store: Store
  ) {}

  public getTemplateId(): Observable<string> {
    return this.store.select(selectTemplateId);
  }

  public getCurrentTemplate(): Observable<ICirculationTemplate> {
    return this.getTemplateId().pipe(
      switchMap((id) => this.circulationService.GetTemplate(id))
    );
  }

  public getMyTemplates(): Observable<ICirculationTemplate[]> {
    return this.circulationService.GetMyTemplates();
  }

  public getCurrentStep(
    template: ICirculationTemplate
  ): Observable<ICirculationTemplateStep> {
    return this.store.select(
      selectMappedStepId((id) => template.steps[Number.parseInt(id, 10) - 1])
    );
  }

  public getPrepareId(): Observable<EntityId> {
    return this.store.select(selectPrepareId);
  }

  public createNewForPrepareId(): Observable<EntityId> {
    return this.getPrepareId().pipe(
      switchMap((id) => this.circulationService.CreateNewFromPrepared(id)),
      retry(2)
    );
  }

  public getTemplates(): Observable<ICirculationTemplate[]> {
    return this.circulationService.GetTemplates();
  }

  public apply(template: ICirculationTemplate): Observable<EntityId> {
    return this.circulationService.ApplyTemplate(template);
  }

  public save(template: ICirculationTemplate): Observable<EntityId> {
    return this.circulationService.SaveCirculationTemplate(template);
  }

  public deleteStep(
    step: ICirculationTemplateStep,
    template: ICirculationTemplate
  ): Observable<void> {
    return this.circulationService.DeleteTemplateStep(step, template);
  }

  public delete(template: ICirculationTemplate): Observable<void> {
    return this.circulationService.DeleteTemplate(template);
  }

  public deleteMetaData(
    metaData: IMetaData,
    template: ICirculationTemplate
  ): Observable<void> {
    return this.circulationService.DeleteTemplateData(metaData, template);
  }
}
